import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DatePicker, Form, Input, InputNumber, Modal, Row, Col, Select, Spin, Switch, Radio, Tag, Alert, Typography, Divider, Table, Button, Space } from "antd";
import { UserOutlined, TeamOutlined, SolutionOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import AmountInputNumber from "../../../../components/inputs/AmountInputNumber";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { libelleGarantie } from "../../../../variables/stringFunctions";

import {
  getDecaissementByCodeId,
  getParametre,
  updateDecaissement,
} from "../../../../hooks/useHooks";
import { PARAMETERS } from "../../../../variables/constants";

const { Text } = Typography;
const PRIMARY = "#242166";

const EMPTY_SELECT_OPTIONS = [];
const REGLEMENT_NATURE_OPTIONS = [
  { label: "Sinistre en principal", value: "SINISTRE EN PRINCIPAL" },
  { label: "Frais accessoires", value: "ACCESSOIRES" },
  { label: "Honoraires", value: "HONORAIRES" },
  { label: "Provision", value: "PROVISION" },
];

// Libellé + couleur + droits d'édition par statut du décaissement.
const ETAT_CONFIG = {
  EN_ATTENTE: { label: "En attente", color: "gold", reglement: true, cheque: false, representant: true },
  REJETE: { label: "Rejeté", color: "volcano", reglement: true, cheque: false, representant: true },
  VALIDE: { label: "Validé", color: "blue", reglement: false, cheque: true, representant: true },
  EN_ATTENTE_D_ENCAISSEMENT: { label: "En attente d'encaissement", color: "geekblue", reglement: false, cheque: true, representant: true },
  CHEQUE_ANNULE: { label: "Chèque annulé", color: "orange", reglement: false, cheque: true, representant: true },
  ENCAISSE: { label: "Encaissé", color: "green", reglement: false, cheque: false, representant: false },
};

const dedupeOptions = (options = []) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option || !option.value) return false;
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const sanitizeOption = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const value = `${rawValue}`.trim();
  if (!value || value.toLowerCase() === "ajout du parametre") return null;
  return { label: value, value };
};

const fullName = (p) => `${p?.nom || ""} ${p?.prenom || ""}`.trim();

// Clé d'une garantie pour la ventilation (codeId si présent, sinon nom).
const gkeyVent = (g) => g?.garantieCodeId || g?.garantieNom || "?";

const EditDecaissementModal = ({ codeId, open, onClose, onUpdated, sinistre }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decaissement, setDecaissement] = useState(null);
  const [motifs, setMotifs] = useState(EMPTY_SELECT_OPTIONS);
  const [motifsLoading, setMotifsLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState(EMPTY_SELECT_OPTIONS);
  const [banksLoading, setBanksLoading] = useState(false);
  const [natureActeOptions, setNatureActeOptions] = useState(EMPTY_SELECT_OPTIONS);
  const [natureActeLoading, setNatureActeLoading] = useState(false);

  // Choix du destinataire du chèque (victime / représentant)
  const [chequeMode, setChequeMode] = useState("VICTIME");
  const [chequeRepresentant, setChequeRepresentant] = useState(null);
  // Victimes du sinistre (chargées à part pour garantir représentant + ayants droit)
  const [sinistreVictimes, setSinistreVictimes] = useState([]);
  // Ventilation par garantie (suivi du SAP par garantie) : référentiel + montants imputés.
  const [sapGarantiesVent, setSapGarantiesVent] = useState([]);
  const [ventMontants, setVentMontants] = useState({});

  // ── Statut & droits d'édition ───────────────────────────────────────────
  const etat = decaissement?.etatDecaissement || "EN_ATTENTE";
  const cfg = ETAT_CONFIG[etat] || ETAT_CONFIG.EN_ATTENTE;
  const canEditReglement = cfg.reglement; // montant, nature, motif, date…
  const canEditCheque = cfg.cheque;       // n° chèque, banque, date émission
  const canEditRepresentant = cfg.representant;

  const beneficiaireLabel = useMemo(() => {
    const benef = decaissement?.beneficiaire;
    if (!benef) return "";
    const nom = benef.nom || benef.libelle || benef?.personne?.nom || "";
    const prenom = benef.prenom || benef?.personne?.prenom || "";
    const raisonSociale = benef.raisonSociale || "";
    return [nom, prenom, raisonSociale].filter(Boolean).join(" ").trim();
  }, [decaissement]);

  // Victime liée (pour proposer représentant / ayants droit) — résolue depuis le sinistre.
  // Source des victimes : prop sinistre > payload décaissement > fetch de secours.
  const linkedVictime = useMemo(() => {
    const benef = decaissement?.beneficiaire;
    if (!benef) return null;
    const victimes = (sinistre?.victimes?.length ? sinistre.victimes
      : decaissement?.sinistre?.victimes?.length ? decaissement.sinistre.victimes
        : sinistreVictimes) || [];
    if (!victimes.length) return null;
    const benefName = `${benef?.nom || ""} ${benef?.prenom || ""}`.trim().toLowerCase();
    return victimes.find((v) => {
      const p = v?.personne;
      if (!p) return false;
      if (benef?.codeId && p?.codeId === benef.codeId) return true;
      const pName = `${p?.nom || ""} ${p?.prenom || ""}`.trim().toLowerCase();
      return benefName && pName === benefName;
    }) || null;
  }, [decaissement, sinistre, sinistreVictimes]);

  const representantCandidates = useMemo(() => {
    if (!linkedVictime) return [];
    const list = [];
    // Principal (par défaut = le plus récent) en premier.
    if (linkedVictime?.representant && fullName(linkedVictime.representant)) {
      list.push({ value: fullName(linkedVictime.representant), role: "Représentant (par défaut)" });
    }
    // Tous les représentants du vivier (plusieurs possibles).
    (linkedVictime?.representants || []).forEach((r) => {
      const n = fullName(r);
      if (n && !list.some((c) => c.value === n)) list.push({ value: n, role: "Représentant" });
    });
    (linkedVictime?.ayantsDroit || []).forEach((a) => {
      const n = fullName(a);
      if (n && !list.some((c) => c.value === n)) list.push({ value: n, role: "Ayant droit" });
    });
    // Conserver l'éventuel représentant déjà enregistré même s'il n'est plus dans la liste.
    if (decaissement?.representant && !list.some((c) => c.value === decaissement.representant)) {
      list.push({ value: decaissement.representant, role: "Actuel" });
    }
    return list;
  }, [linkedVictime, decaissement]);

  const isVictimeBeneficiary = !!linkedVictime;
  const victimeName = fullName(linkedVictime?.personne) || beneficiaireLabel || "la victime";

  const natureOptions = useMemo(() => {
    if (!decaissement?.nature) return REGLEMENT_NATURE_OPTIONS;
    const exists = REGLEMENT_NATURE_OPTIONS.some((o) => o.value === decaissement.nature);
    return exists ? REGLEMENT_NATURE_OPTIONS : [...REGLEMENT_NATURE_OPTIONS, { label: decaissement.nature, value: decaissement.nature }];
  }, [decaissement]);

  // Taux + montant retenu : uniquement pour un expert (honoraires).
  const watchedNature = Form.useWatch("nature", form);
  const showHonoraireFields =
    decaissement?.expertise === true ||
    decaissement?.beneficiaire?.fonction === "EXPERT" ||
    watchedNature === "HONORAIRES" ||
    decaissement?.nature === "HONORAIRES";

  // Montant courant (surveillé) et ventilation par garantie.
  const watchedMontant = Form.useWatch("montantGlobal", form);
  const montantCourant = Number(watchedMontant) || 0;
  const sommeVentilation = Object.values(ventMontants).reduce((a, v) => a + (Number(v) || 0), 0);
  const repartirVentilationProrataSap = () => {
    const totalSap = sapGarantiesVent.reduce((a, g) => a + (Number(g.sap) || 0), 0);
    const next = {};
    if (totalSap > 0 && montantCourant > 0) {
      sapGarantiesVent.forEach((g) => { next[gkeyVent(g)] = Math.round((Number(g.sap) || 0) / totalSap * montantCourant); });
      const s = Object.values(next).reduce((a, v) => a + v, 0);
      const diff = Math.round(montantCourant) - s;
      if (diff !== 0 && sapGarantiesVent.length) {
        const gmax = sapGarantiesVent.reduce((m, g) => ((next[gkeyVent(g)] || 0) > (next[gkeyVent(m)] || 0) ? g : m), sapGarantiesVent[0]);
        next[gkeyVent(gmax)] = (next[gkeyVent(gmax)] || 0) + diff;
      }
    }
    setVentMontants(next);
  };

  // ── Chargement des paramètres ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setMotifsLoading(true);
    getParametre(PARAMETERS.motifReglement)
      .then(({ data }) => setMotifs((data || []).map((m) => ({ label: m.valeur, value: m.valeur }))))
      .catch(() => setMotifs(EMPTY_SELECT_OPTIONS))
      .finally(() => setMotifsLoading(false));

    setBanksLoading(true);
    getParametre(PARAMETERS.banque)
      .then(({ data }) => setBankOptions(dedupeOptions((data || []).map((b) => sanitizeOption(b?.libelleParametre ?? b?.valeur ?? b?.label ?? b?.libelle ?? b?.nom ?? b?.value)).filter(Boolean))))
      .catch(() => setBankOptions(EMPTY_SELECT_OPTIONS))
      .finally(() => setBanksLoading(false));

    setNatureActeLoading(true);
    getParametre(PARAMETERS.natureActe)
      .then(({ data }) => setNatureActeOptions(dedupeOptions((data || []).map((n) => sanitizeOption(n?.libelleParametre ?? n?.valeur ?? n?.label ?? n?.libelle ?? n?.nom ?? n?.value)).filter(Boolean))))
      .catch(() => setNatureActeOptions(EMPTY_SELECT_OPTIONS))
      .finally(() => setNatureActeLoading(false));
  }, [open]);

  useEffect(() => {
    if (decaissement?.banque) {
      setBankOptions((prev) => {
        const option = sanitizeOption(decaissement.banque);
        return option ? dedupeOptions([...(prev || []), option]) : prev;
      });
    }
    if (decaissement?.natureActe) {
      setNatureActeOptions((prev) => {
        const option = sanitizeOption(decaissement.natureActe);
        return option ? dedupeOptions([...(prev || []), option]) : prev;
      });
    }
  }, [decaissement]);

  // ── Chargement du décaissement ──────────────────────────────────────────
  useEffect(() => {
    if (open && codeId) {
      setLoading(true);
      getDecaissementByCodeId(codeId)
        .then(({ data }) => {
          setDecaissement(data);
          // Initialise le choix du chèque : représentant si déjà renseigné.
          setChequeMode(data?.representant ? "REPRESENTANT" : "VICTIME");
          setChequeRepresentant(data?.representant || null);
          // Ventilation par garantie : référentiel du dossier + montants déjà imputés.
          const initVent = {};
          (data?.ventilation || []).forEach((v) => {
            const k = v?.garantieCodeId || v?.garantieNom;
            if (k) initVent[k] = v.montant;
          });
          setVentMontants(initVent);
          const scIdVent = sinistre?.codeId || data?.sinistre?.codeId;
          if (scIdVent) {
            axiosInstance.get(`/api/sinistre/sap-garanties/${scIdVent}`)
              .then((r) => setSapGarantiesVent(r.data || []))
              .catch(() => setSapGarantiesVent([]));
          }
          // Victimes : prop sinistre prioritaire ; sinon payload ; sinon fetch de secours.
          const sinistreCodeId = sinistre?.codeId || data?.sinistre?.codeId;
          if (!(sinistre?.victimes?.length) && !(data?.sinistre?.victimes?.length) && sinistreCodeId) {
            axiosInstance.get(`/api/sinistre/codeId/${sinistreCodeId}`)
              .then((res) => setSinistreVictimes(res.data?.victimes || []))
              .catch(() => setSinistreVictimes([]));
          } else {
            setSinistreVictimes(sinistre?.victimes || data?.sinistre?.victimes || []);
          }
          form.setFieldsValue({
            montantGlobal: data?.montantGlobal ?? 0,
            taxes: data?.taxes ?? 0,
            taux: data?.taux ?? 0,
            motifReglement: data?.motifReglement ?? "",
            formuleDeFin: data?.formuleDeFin ?? "",
            nature: data?.nature ?? undefined,
            natureActe: data?.natureActe ?? undefined,
            numeroCheque: data?.numeroCheque ?? "",
            banque: data?.banque ?? undefined,
            dateEmissionCheque: data?.dateEmissionCheque ? dayjs(data.dateEmissionCheque) : null,
            commentaire: data?.commentaire ?? data?.observations ?? "",
            dateReglement: data?.dateReglement ? dayjs(data.dateReglement) : null,
          });
        })
        .catch(() => {
          toast.error("Impossible de charger le décaissement sélectionné");
          handleClose();
        })
        .finally(() => setLoading(false));
    }
    if (!open) {
      form.resetFields();
      setDecaissement(null);
      setSinistreVictimes([]);
      setSapGarantiesVent([]);
      setVentMontants({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, codeId]);

  const handleClose = () => {
    form.resetFields();
    setDecaissement(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!decaissement) return;
    try {
      const values = await form.validateFields();
      const num = (val, fb = 0) => {
        if (val === null || val === undefined || val === "") return fb;
        const n = Number(val);
        return Number.isNaN(n) ? fb : n;
      };
      // Ventilation facultative : si renseignée, Σ doit égaler le montant du règlement.
      const montantFinal = canEditReglement ? num(values.montantGlobal) : num(decaissement.montantGlobal);
      if (sommeVentilation > 0 && Math.round(sommeVentilation) !== Math.round(montantFinal)) {
        toast.error(`La ventilation par garantie (${Number(sommeVentilation).toLocaleString("fr-FR")} F) doit égaler le montant du règlement (${Number(montantFinal).toLocaleString("fr-FR")} F).`);
        return;
      }
      setSaving(true);
      // Représentant : pris en compte uniquement si modifiable.
      const representant = canEditRepresentant
        ? (chequeMode === "REPRESENTANT" ? (chequeRepresentant || null) : null)
        : (decaissement.representant || null);
      // Ayant droit ? (préfixe « AD » sur le document) — déduit du rôle du candidat choisi ;
      // pour un choix « Actuel » (préservé) on garde le flag existant.
      const chosenRole = representantCandidates.find((c) => c.value === chequeRepresentant)?.role;
      const representantAyantDroit = canEditRepresentant
        ? (chequeMode === "REPRESENTANT"
          ? (chosenRole === "Ayant droit" ? true
            : chosenRole === "Actuel" ? !!decaissement.representantAyantDroit : false)
          : false)
        : !!decaissement.representantAyantDroit;

      const payload = {
        ...decaissement,
        // Champs règlement (verrouillés hors EN_ATTENTE/REJETE → on garde l'existant)
        montantGlobal: canEditReglement ? num(values.montantGlobal) : decaissement.montantGlobal,
        taxes: canEditReglement ? num(values.taxes) : decaissement.taxes,
        taux: canEditReglement ? num(values.taux) : decaissement.taux,
        motifReglement: canEditReglement ? (values.motifReglement ?? "") : decaissement.motifReglement,
        formuleDeFin: canEditReglement ? (values.formuleDeFin ?? "") : decaissement.formuleDeFin,
        nature: canEditReglement ? (values.nature ?? "") : decaissement.nature,
        natureActe: canEditReglement ? (values.natureActe ?? "") : decaissement.natureActe,
        dateReglement: canEditReglement
          ? (values.dateReglement ? values.dateReglement.toISOString() : null)
          : decaissement.dateReglement,
        // Champs chèque (éditables une fois validé / à encaisser / annulé)
        numeroCheque: canEditCheque ? (values.numeroCheque || null) : decaissement.numeroCheque,
        banque: canEditCheque ? (values.banque || null) : decaissement.banque,
        dateEmissionCheque: canEditCheque
          ? (values.dateEmissionCheque ? values.dateEmissionCheque.toISOString() : null)
          : decaissement.dateEmissionCheque,
        representant,
        representantAyantDroit,
        commentaire: values.commentaire || null,
        observations: values.commentaire || null,
      };

      const response = await updateDecaissement(payload);
      // Ventilation par garantie (facultative) : imputée après la mise à jour du montant.
      const ventLines = sapGarantiesVent
        .map((g) => ({ garantieCodeId: g.garantieCodeId || null, garantieNom: g.garantieNom || null, montant: Number(ventMontants[gkeyVent(g)]) || 0 }))
        .filter((l) => l.montant > 0);
      if (ventLines.length && decaissement?.codeId) {
        try {
          await axiosInstance.put(`/api/decaissement/${decaissement.codeId}/ventilation`, ventLines);
        } catch (ventErr) {
          toast.warn("Règlement mis à jour, mais la ventilation par garantie a échoué.");
        }
      }
      toast.success("Décaissement mis à jour avec succès");
      setSaving(false);
      handleClose();
      if (onUpdated) onUpdated(response?.data || payload);
      else window.location.reload();
    } catch (error) {
      setSaving(false);
      if (error?.errorFields) return;
      toast.error("Une erreur est survenue lors de la mise à jour");
    }
  };

  const handleMotifChange = (value) => {
    if (value) form.setFieldsValue({ formuleDeFin: value, motifReglement: value });
    else form.setFieldsValue({ formuleDeFin: undefined });
  };

  const lockHint = (editable) =>
    editable ? null : <Tag icon={<LockOutlined />} color="default" style={{ marginLeft: 6 }}>Verrouillé</Tag>;

  return (
    <Modal
      title={<span style={{ color: PRIMARY, fontWeight: 700 }}>Éditer le règlement</span>}
      open={open}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={saving}
      width={920}
      zIndex={2000}
      onOk={handleSubmit}
      onCancel={handleClose}
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}><Spin /></div>
      ) : (
        <Form layout="vertical" form={form} requiredMark={false}>
          {/* Bandeau statut : indique ce qui est modifiable */}
          <Alert
            type={etat === "ENCAISSE" ? "success" : etat === "EN_ATTENTE" ? "warning" : "info"}
            showIcon
            style={{ marginBottom: 16 }}
            message={<span>Statut du décaissement : <Tag color={cfg.color}>{cfg.label}</Tag></span>}
            description={
              etat === "ENCAISSE"
                ? "Décaissement encaissé : informations en lecture seule (seul le commentaire reste modifiable)."
                : canEditReglement
                  ? "Le règlement n'est pas encore validé : montant, nature, motif, date et destinataire du chèque sont modifiables."
                  : "Chèque émis : le montant et la nature sont verrouillés ; vous pouvez ajuster les informations du chèque (n°, banque, date d'émission) et le destinataire."
            }
          />

          {/* Bénéficiaire */}
          {beneficiaireLabel && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Bénéficiaire (victime)">
                  <Input value={beneficiaireLabel} disabled />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Choix du destinataire du chèque (si la victime est résolue) */}
          {isVictimeBeneficiary && (
            <div style={{ marginBottom: 16, padding: 16, border: `1px solid ${PRIMARY}22`, borderRadius: 12, background: `${PRIMARY}08` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <SolutionOutlined style={{ color: PRIMARY }} />
                <Text strong style={{ color: PRIMARY }}>À l'ordre de qui établir l'ordre de dépense / le chèque ?</Text>
                {linkedVictime?.decesSinistre ? <Tag color="red">Victime décédée</Tag> : linkedVictime?.blesseSinistre ? <Tag color="orange">Victime blessée</Tag> : null}
                {lockHint(canEditRepresentant)}
              </div>
              <Radio.Group
                value={chequeMode}
                onChange={(e) => setChequeMode(e.target.value)}
                disabled={!canEditRepresentant}
                style={{ display: "flex", gap: 10, width: "100%", flexWrap: "wrap" }}
              >
                {[
                  { key: "VICTIME", icon: <UserOutlined />, title: "La victime", sub: victimeName, disabled: false },
                  { key: "REPRESENTANT", icon: <TeamOutlined />, title: "Un représentant", sub: representantCandidates.length ? "Représentant / ayant droit" : "Aucun disponible", disabled: !representantCandidates.length },
                ].map((o) => {
                  const active = chequeMode === o.key;
                  return (
                    <label key={o.key} style={{
                      flex: 1, minWidth: 200, cursor: o.disabled || !canEditRepresentant ? "not-allowed" : "pointer",
                      border: `1.5px solid ${active ? PRIMARY : "#e5e7eb"}`, background: "#fff",
                      opacity: o.disabled ? 0.55 : 1, borderRadius: 10, padding: "10px 12px",
                    }}>
                      <Radio value={o.key} disabled={o.disabled || !canEditRepresentant} />
                      <span style={{ marginLeft: 4, fontWeight: 600 }}>{o.icon} {o.title}</span>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{o.sub}</div>
                    </label>
                  );
                })}
              </Radio.Group>

              {chequeMode === "REPRESENTANT" && representantCandidates.length > 0 && (
                <Select
                  style={{ width: "100%", marginTop: 12 }}
                  value={chequeRepresentant}
                  onChange={setChequeRepresentant}
                  disabled={!canEditRepresentant}
                  placeholder="Choisir le représentant"
                  options={representantCandidates.map((c) => ({
                    value: c.value,
                    label: <span>{c.value} <Tag style={{ marginLeft: 6 }} color={c.role === "Représentant" ? "geekblue" : c.role === "Actuel" ? "default" : "gold"}>{c.role}</Tag></span>,
                  }))}
                />
              )}

              <div style={{ marginTop: 12, padding: "8px 12px", background: "#fff", borderRadius: 8, border: "1px solid #eef0f4" }}>
                <Text style={{ fontSize: 12 }}>
                  À l'ordre de <Text strong>{chequeMode === "REPRESENTANT" ? (chequeRepresentant || "…") : victimeName}</Text>
                  {chequeMode === "REPRESENTANT" && <> — pour le compte de <Text strong>{victimeName}</Text></>}.
                </Text>
              </div>
            </div>
          )}

          {/* Représentant en texte libre (fallback si victime non résolue) */}
          {!isVictimeBeneficiary && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label={<span>Représentant {lockHint(canEditRepresentant)}</span>}>
                  <Input
                    value={chequeRepresentant || ""}
                    disabled={!canEditRepresentant}
                    onChange={(e) => { setChequeMode(e.target.value ? "REPRESENTANT" : "VICTIME"); setChequeRepresentant(e.target.value || null); }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Divider orientation="left" style={{ color: PRIMARY }}>Règlement {lockHint(canEditReglement)}</Divider>

          <Row gutter={16}>
            <Col xs={24} md={showHonoraireFields ? 12 : 24}>
              <Form.Item label="Montant TTC (FCFA)" name="montantGlobal" rules={[{ required: true, message: "Veuillez renseigner le montant" }]}>
                <AmountInputNumber min={0} style={{ width: "100%" }} disabled={!canEditReglement} />
              </Form.Item>
            </Col>
            {showHonoraireFields && (
              <Col xs={24} md={12}>
                <Form.Item label="Montant retenu / Taxes (FCFA)" name="taxes">
                  <AmountInputNumber min={0} style={{ width: "100%" }} disabled={!canEditReglement} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={16}>
            {showHonoraireFields && (
              <Col xs={24} md={12}>
                <Form.Item label="Taux (%)" name="taux">
                  <InputNumber min={0} max={100} style={{ width: "100%" }} disabled={!canEditReglement} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={showHonoraireFields ? 12 : 24}>
              <Form.Item label="Nature" name="nature">
                <Select allowClear placeholder="Sélectionner la nature" options={natureOptions} showSearch optionFilterProp="label" disabled={!canEditReglement} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Motif (sélection)" name="formuleDeFin">
                <Select allowClear loading={motifsLoading} onChange={handleMotifChange} options={motifs} placeholder="Sélectionner un motif" showSearch optionFilterProp="label" disabled={!canEditReglement} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date de règlement" name="dateReglement">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabled={!canEditReglement} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Motif (description)" name="motifReglement">
                <Input.TextArea rows={4} disabled={!canEditReglement} />
              </Form.Item>
            </Col>
          </Row>

          {/* Ventilation par garantie (facultative) : imputer le montant aux garanties du dossier
              pour suivre le SAP par garantie. */}
          {sapGarantiesVent.length > 0 && (
            <>
              <Divider orientation="left" style={{ color: PRIMARY }}>Ventilation par garantie <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>(facultatif)</span></Divider>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Précisez la/les garantie(s) réglée(s). Si renseignée, la somme doit égaler le montant du règlement.
                </div>
                <Space wrap>
                  <Button size="small" onClick={repartirVentilationProrataSap} disabled={!(montantCourant > 0)}>Répartir au prorata du SAP</Button>
                  <Button size="small" onClick={() => setVentMontants({})}>Effacer</Button>
                  <Tag color={sommeVentilation === 0 ? "default" : (Math.round(sommeVentilation) === Math.round(montantCourant) ? "green" : "orange")}>
                    Ventilé : {Number(sommeVentilation).toLocaleString("fr-FR")} / {Number(montantCourant).toLocaleString("fr-FR")} F
                  </Tag>
                </Space>
              </div>
              <Table rowKey={(r) => gkeyVent(r)} size="small" pagination={false} dataSource={sapGarantiesVent}
                style={{ marginBottom: 8 }}
                columns={[
                  { title: "Garantie", dataIndex: "garantieNom", render: (v, r) => libelleGarantie(v) || r.garantieCodeId || "—" },
                  { title: "Provision", dataIndex: "provision", align: "right", render: (v) => `${Number(v || 0).toLocaleString("fr-FR")} F` },
                  { title: "SAP", dataIndex: "sap", align: "right", render: (v) => <b style={{ color: (Number(v) || 0) > 0 ? "#b91c1c" : "#16a34a" }}>{Number(v || 0).toLocaleString("fr-FR")} F</b> },
                  {
                    title: "Montant à imputer", key: "m", align: "right", width: 190,
                    render: (_, g) => (
                      <AmountInputNumber style={{ width: "100%" }} value={ventMontants[gkeyVent(g)] || 0}
                        onChange={(val) => setVentMontants((m) => ({ ...m, [gkeyVent(g)]: val || 0 }))} />
                    ),
                  },
                ]} />
            </>
          )}

          <Divider orientation="left" style={{ color: PRIMARY }}>Chèque {lockHint(canEditCheque)}</Divider>

          {etat === "EN_ATTENTE" ? (
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Le chèque n'est pas encore émis pour ce décaissement." />
          ) : (
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Numéro de chèque" name="numeroCheque">
                  <Input disabled={!canEditCheque} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Banque" name="banque">
                  <Select allowClear loading={banksLoading} placeholder="Sélectionner la banque" options={bankOptions} showSearch optionFilterProp="label" disabled={!canEditCheque} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Date d'émission" name="dateEmissionCheque">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabled={!canEditCheque} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Divider orientation="left" style={{ color: PRIMARY }}>Suivi</Divider>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Commentaire / Partie adverse" name="commentaire">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Décaissement encaissé">
                <Switch checked={!!decaissement?.encaissement} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Expertise">
                <Switch checked={!!decaissement?.expertise} disabled />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

EditDecaissementModal.propTypes = {
  codeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdated: PropTypes.func,
  sinistre: PropTypes.object,
};

export default EditDecaissementModal;
